import React, { useMemo, useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import KeepCase from './common/KeepCase';

type ExpandedMap = Record<string, boolean>;

interface MarketplaceProps {
  mode?: 'full' | 'embedded';
  isExpanded?: boolean;
  setIsExpanded?: (next: boolean) => void;
  lockedOpen?: boolean;
  setLockedOpen?: (next: boolean) => void;
  expandedTiles?: ExpandedMap;
  setExpandedTiles?: (updater: (prev: ExpandedMap) => ExpandedMap) => void;
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const MarketplaceSection = styled.section`
  width: 100%;
  padding: 5rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(135deg, rgba(200, 167, 94, 0.05) 0%, rgba(26, 26, 26, 0.05) 100%);
`;

const MarketplaceContainer = styled.div`
  max-width: 1000px;
  width: 100%;
  text-align: center;
  padding: 3rem;
  border-radius: 20px;
  background: white;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.07);
  animation: ${fadeIn} 1.2s ease-out;
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 1px solid rgba(200, 167, 94, 0.05);
  margin: 0 auto;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 6px;
    background: linear-gradient(to right, var(--brand-gold-alt), var(--brand-black));
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -100px;
    right: -100px;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(200, 167, 94, 0.1) 0%, transparent 70%);
    border-radius: 50%;
    z-index: 0;
    transition: all 0.5s ease;
    opacity: 0;
  }
  
  &:hover {
    transform: translateY(-12px) scale(1.02);
    box-shadow: 0 30px 60px rgba(200, 167, 94, 0.15);
    border-color: rgba(200, 167, 94, 0.2);
    
    &::before { opacity: 1; }
    &::after { opacity: 1; bottom: -60px; right: -60px; }
    
    svg { transform: rotate(0) scale(1.1); opacity: 0.8; }
  }
  
  &:active {
    transform: translateY(-5px) scale(0.99);
    transition: all 0.2s ease;
  }
  
  svg {
    position: absolute;
    bottom: 20px;
    right: 20px;
    width: 60px;
    height: 60px;
    opacity: 0.3;
    transform: rotate(15deg) scale(1);
    transition: all 0.4s ease;
    color: var(--brand-gold-alt);
    filter: drop-shadow(0 3px 6px rgba(200, 167, 94, 0.2));
  }
`;

const MarketplaceTitle = styled.h2`
  font-family: 'Satoshi', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  color: #333;
  margin-bottom: 1.5rem;
  position: relative;
  transition: transform 0.3s ease, color 0.3s ease;
  text-transform: lowercase;
  
  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background: linear-gradient(to right, var(--brand-gold-alt), var(--brand-black));
    margin: 1rem auto 0;
    border-radius: 4px;
    transition: width 0.3s ease;
  }
  
  ${MarketplaceContainer}:hover &::after { width: 80px; }
`;

const MarketplaceText = styled.p`
  color: #666;
  line-height: 1.8;
  font-size: clamp(1.1rem, 2vw, 1.25rem);
  max-width: 800px;
  margin: 0 auto;
  transition: color 0.3s ease;
  position: relative;
  z-index: 1;
  text-align: center;
`;

const MarketplaceToggle = styled.button`
  background: none;
  color: var(--brand-gold-alt);
  border: 1px solid rgba(200, 167, 94, 0.3);
  padding: 0.5rem 1.25rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  margin: 0.75rem auto 0;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
  text-transform: lowercase;
  letter-spacing: 0.03em;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  
  &:hover {
    background: rgba(200, 167, 94, 0.08);
    border-color: rgba(200, 167, 94, 0.5);
    transform: translateY(-1px);
  }
  
  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.3s ease;
    position: static;
    opacity: 1;
    filter: none;
    color: var(--brand-gold-alt);
  }
  
  &[data-expanded="true"] svg { transform: rotate(180deg); }
`;

const MarketplaceTilesContainer = styled.div<{ isExpanded: boolean }>`
  max-height: ${props => props.isExpanded ? '4000px' : '0'};
  overflow: hidden;
  transition: max-height 0.5s ease-in-out;
  margin-top: ${props => props.isExpanded ? '2rem' : '0'};
  width: 100%;
  
  @media (max-width: 768px) {
    max-height: ${props => props.isExpanded ? '6000px' : '0'};
    margin-top: ${props => props.isExpanded ? '1.5rem' : '0'};
  }
`;

const MarketplaceTilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 0 1rem;
  }
  
  @media (max-width: 480px) {
    gap: 1.25rem;
    padding: 0 0.75rem;
  }
`;

const MarketplaceTile = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(200, 167, 94, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    border-radius: 10px;
    margin-bottom: 0.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 0.75rem;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg, var(--brand-gold-alt), var(--brand-black));
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(200, 167, 94, 0.15);
    border-color: rgba(200, 167, 94, 0.2);
    
    &::before { opacity: 1; }
  }
`;

const TileHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    gap: 0.875rem;
    margin-bottom: 0.875rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }
`;

const TileIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--brand-gold-alt), var(--brand-black));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg { width: 20px; height: 20px; color: white; }
  
  @media (max-width: 768px) {
    width: 36px; height: 36px; border-radius: 8px;
    svg { width: 18px; height: 18px; }
  }
  @media (max-width: 480px) {
    width: 32px; height: 32px; border-radius: 6px;
    svg { width: 16px; height: 16px; }
  }
`;

const TileContent = styled.div`
  flex: 1;
`;

const TileTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
`;

const TileRevenue = styled.div`
  font-size: 0.85rem;
  color: var(--brand-gold-alt);
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const TileDescription = styled.p<{ isVisible: boolean }>`
  font-size: 0.9rem;
  color: #666;
  line-height: 1.5;
  margin: 0;
  max-height: ${props => props.isVisible ? '300px' : '0'};
  opacity: ${props => props.isVisible ? '1' : '0'};
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  margin-top: ${props => props.isVisible ? '0.75rem' : '0'};
`;

type Agent = {
  id: string;
  title: string;
  revenue: string;
  description: React.ReactNode;
  icon: React.ReactNode;
};

const defaultMarketplaceAgents: Agent[] = [
  {
    id: 'ai-research',
    title: 'ai legal deep research',
    revenue: '',
    description: 'instant, citation-grounded research on case law, statutes, and regulatory guidance.',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>)
  },
  {
    id: 'custom-tools',
    title: 'customize your own tools',
    revenue: '',
    description: 'build and deploy custom AI tools tailored to your firm\'s unique workflows, practice areas, and client needs.',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>)
  },
  {
    id: 'document-creator',
    title: 'ai legal document creator',
    revenue: '',
    description: 'court-ready documents generated from naturally spoken conversations.',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/></svg>)
  },
  {
    id: 'document-intel',
    title: 'client document intelligence',
    revenue: '',
    description: 'scans client uploads, extracting key facts in seconds.',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10,9 9,9 8,9"/></svg>)
  },
  {
    id: 'bilingual-ai',
    title: 'bilingual ai',
    revenue: '',
    description: 'native english and spanish conversation, translation, and document generation.',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/></svg>)
  },
  {
    id: 'autonomous-agent',
    title: 'autonomous ai agent',
    revenue: '',
    description: (<><KeepCase>Prest0n</KeepCase> never sleeps. handles firm notifications, slack, email, and workflows 24/7.</>),
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>)
  },
  {
    id: 'voice-calling',
    title: 'ai voice calling',
    revenue: '',
    description: 'inbound and outbound voice capabilities to capture every lead.',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>)
  },
  {
    id: 'sms-comms',
    title: 'sms client communications',
    revenue: '',
    description: 'text message automation for reminders, follow-ups, and matter updates.',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>)
  },
  {
    id: 'calendar-intel',
    title: 'calendar intelligence',
    revenue: '',
    description: 'proactive conflict alerts and automated scheduling.',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>)
  },
  {
    id: 'app-integrations',
    title: 'integrates with any system',
    revenue: '',
    description: 'sits on top of the tools you already use: slack, gmail, drive, calendars, and anything else.',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>)
  },
  {
    id: 'billing-onboarding',
    title: 'billing & self-service',
    revenue: '',
    description: 'transparent stripe-integrated billing and client onboarding capabilities.',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M16 12h.01"/><path d="M2 10h20"/></svg>)
  },
  {
    id: 'practice-marketing',
    title: 'automated practice marketing',
    revenue: '',
    description: 'social media automation and consistency on linkedin and x.',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>)
  },
  {
    id: 'video-gen',
    title: 'video script generation',
    revenue: '',
    description: 'ai generates video scripts tailored to your firm and audience.',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>)
  },
  {
    id: 'enterprise-security',
    title: 'enterprise security',
    revenue: '',
    description: 'strong isolation, audit logs, and prompt injection protection.',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l7 3v6c0 5-3.5 10-7 11-3.5-1-7-6-7-11V5l7-3z"/></svg>)
  },
  {
    id: 'bug-squasher',
    title: 'automated self-healing',
    revenue: '',
    description: 'continuous deployment testing and bug detection so your portal never goes down.',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1v22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>)
  }
];

export default function Marketplace(props: MarketplaceProps): JSX.Element {
  const { mode = 'full' } = props;

  const [internalExpanded, setInternalExpanded] = useState(true);
  const [internalLocked, setInternalLocked] = useState(true);
  const [internalExpandedTiles, setInternalExpandedTiles] = useState<ExpandedMap>({});

  const isExpanded = props.isExpanded ?? internalExpanded;
  const setIsExpanded = props.setIsExpanded ?? setInternalExpanded;
  const lockedOpen = props.lockedOpen ?? internalLocked;
  const setLockedOpen = props.setLockedOpen ?? setInternalLocked;
  const expandedTiles = props.expandedTiles ?? internalExpandedTiles;
  const setExpandedTiles = props.setExpandedTiles ?? ((updater: (prev: ExpandedMap) => ExpandedMap) => setInternalExpandedTiles(prev => updater(prev)));

  const agents = useMemo(() => defaultMarketplaceAgents, []);

  const toggleTiles = () => {
    const next = !isExpanded;
    setIsExpanded(next);
    setLockedOpen(next);
    try {
      localStorage.setItem('marketplace_expanded', next ? '1' : '0');
      localStorage.setItem('marketplace_locked', next ? '1' : '0');
    } catch {}
  };

  const handleTileToggle = (tileId: string) => {
    setExpandedTiles(prev => {
      const updated = { ...prev, [tileId]: !prev[tileId] };
      try { localStorage.setItem('marketplace_tiles', JSON.stringify(updated)); } catch {}
      return updated;
    });
  };

  const handleTileHover = (tileId: string, hovering: boolean) => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    if (!isMobile) {
      setExpandedTiles(prev => {
        const updated = { ...prev, [tileId]: hovering };
        try { localStorage.setItem('marketplace_tiles', JSON.stringify(updated)); } catch {}
        return updated;
      });
    }
  };

  React.useEffect(() => {
    try {
      const persistedExpanded = localStorage.getItem('marketplace_expanded');
      const persistedLocked = localStorage.getItem('marketplace_locked');
      const persistedTiles = localStorage.getItem('marketplace_tiles');
      if (persistedExpanded != null) {
        setIsExpanded(persistedExpanded === '1');
      } else {
        setIsExpanded(true); // default expanded
      }
      if (persistedLocked != null) {
        setLockedOpen(persistedLocked === '1');
      } else {
        setLockedOpen(true);
      }
      if (persistedTiles) {
        const parsed = JSON.parse(persistedTiles) as ExpandedMap;
        setExpandedTiles(() => parsed);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const container = (
    <>
      <MarketplaceContainer>
        <MarketplaceTitle>built-in tools — a sample of thousands</MarketplaceTitle>
        <MarketplaceToggle onClick={toggleTiles} data-expanded={isExpanded ? 'true' : 'false'}>
          {isExpanded ? 'hide tools' : 'show tools'}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </MarketplaceToggle>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 3h18l-2 13H5L3 3z" />
          <path d="M16 16a2 2 0 1 1-4 0" />
          <path d="M8 16a2 2 0 1 1-4 0" />
          <path d="M9 5v6l3-3 3 3V5" />
        </svg>
      </MarketplaceContainer>

      <MarketplaceTilesContainer isExpanded={isExpanded}>
        <MarketplaceTilesGrid>
          {agents.map(agent => (
            <MarketplaceTile
              key={agent.id}
              onClick={() => handleTileToggle(agent.id)}
              onMouseEnter={() => handleTileHover(agent.id, true)}
              onMouseLeave={() => handleTileHover(agent.id, false)}
            >
              <TileHeader>
                <TileIcon>{agent.icon}</TileIcon>
                <TileContent>
                  <TileTitle>{agent.title}</TileTitle>
                  <TileRevenue>{agent.revenue}</TileRevenue>
                </TileContent>
              </TileHeader>
              <TileDescription isVisible={true}>
                {agent.description}
              </TileDescription>
            </MarketplaceTile>
          ))}
        </MarketplaceTilesGrid>
      </MarketplaceTilesContainer>
    </>
  );

  if (mode === 'embedded') {
    return <div>{container}</div>;
  }
  return (
    <MarketplaceSection>
      {container}
    </MarketplaceSection>
  );
}

// ─── IntegrationsShowcase ────────────────────────────────────────────────────

const scrollLeft = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

const scrollRight = keyframes`
  from { transform: translateX(-50%); }
  to { transform: translateX(0); }
`;

const IntegrationsSection = styled.section`
  width: 100%;
  padding: 4rem 2rem;
  background: white;
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(200, 167, 94, 0.03) 0%, rgba(26, 26, 26, 0.02) 100%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
  @media (max-width: 480px) {
    padding: 2.5rem 0.75rem;
  }
`;

const IntegrationsTitleWrapper = styled.div`
  text-align: center;
  margin-bottom: 2.5rem;
`;

const IntegrationsTitle = styled.h2`
  font-family: 'Space Grotesk', 'Satoshi', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  color: #333;
  margin-bottom: 0.75rem;
  position: relative;
  text-transform: lowercase;

  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background: linear-gradient(to right, var(--brand-gold-alt), var(--brand-black));
    margin: 1rem auto 0;
    border-radius: 4px;
  }
`;

/* subtitle removed per request */

const ScrollRowWrapper = styled.div`
  overflow: hidden;
  width: 100%;
  position: relative;

  &::before, &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 80px;
    z-index: 2;
    pointer-events: none;
  }
  &::before {
    left: 0;
    background: linear-gradient(to right, white, transparent);
  }
  &::after {
    right: 0;
    background: linear-gradient(to left, white, transparent);
  }

  @media (max-width: 768px) {
    &::before, &::after { width: 40px; }
  }
`;

const ScrollTrack = styled.div<{ $direction?: 'left' | 'right'; $duration?: number }>`
  display: flex;
  gap: 2.5rem;
  animation: ${({ $direction }) => $direction === 'right' ? scrollRight : scrollLeft}
    ${({ $duration }) => $duration || 45}s linear infinite;
  width: max-content;
  padding: 0.5rem 0;

  &:hover {
    animation-play-state: paused;
  }
`;

const RowStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const AppItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.95rem;
  font-weight: 500;
  color: #555;
  white-space: nowrap;
  flex-shrink: 0;
  transition: color 0.2s ease, opacity 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  opacity: 0.7;

  &:hover {
    color: var(--brand-black);
    opacity: 1;

    span svg {
      filter: grayscale(0%);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;
    gap: 0.5rem;
  }
`;

const AppIconWrapper = styled.span`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
    filter: grayscale(100%);
    opacity: 0.55;
    transition: filter 0.25s ease, opacity 0.25s ease;
  }

  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
    svg { width: 20px; height: 20px; }
  }
`;

/* Restore color on hover for the parent AppItem */

/* Official brand SVG icons for each integration */
const appIcons: Record<string, JSX.Element> = {
  Gmail: <svg viewBox="0 0 24 24"><path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" fill="#EA4335"/></svg>,
  'Google Calendar': <svg viewBox="0 0 24 24"><path d="M18.316 5.684H24v12.632h-5.684V5.684zM5.684 24h12.632v-5.684H5.684V24zM18.316 5.684V0H5.684v5.684h12.632z" fill="#1967D2"/><path d="M5.684 18.316H0V5.684h5.684v12.632z" fill="#1E8E3E"/><path d="M18.316 5.684H24V0h-5.684v5.684z" fill="#EA4335"/><path d="M5.684 5.684H0V0h5.684v5.684z" fill="#FBBC04"/><path d="M18.316 18.316H24V24h-5.684v-5.684z" fill="#34A853"/><path d="M5.684 18.316H0V24h5.684v-5.684z" fill="#188038"/><path d="M9.2 15.3V9.5h1.5v5.8H9.2zm4.1-3.8c-.4-.3-.9-.5-1.4-.5-.6 0-1.1.2-1.5.7-.4.4-.6 1-.6 1.7s.2 1.3.6 1.7c.4.4.9.7 1.5.7.6 0 1-.2 1.4-.5v.5c0 .7-.5 1.1-1.2 1.1-.5 0-.9-.3-1.1-.7l-1.3.5c.4.8 1.2 1.4 2.4 1.4 1.6 0 2.7-1 2.7-2.7v-4.6h-1.5v.4z" fill="#4285F4"/></svg>,
  'Google Drive': <svg viewBox="0 0 24 24"><path d="M8.267 3h7.466L24 15.4h-7.467L8.267 3z" fill="#0066DA"/><path d="M1.613 21.4 5.48 15.4H24l-3.867 6H1.613z" fill="#00AC47"/><path d="M0 15.4 3.867 21.4 12 7.6 8.133 1.6 0 15.4z" fill="#FFBA00"/></svg>,
  'Google Docs': <svg viewBox="0 0 24 24"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" fill="#4285F4"/><path d="M14.5 2V7.5H20" fill="#A1C2FA"/><path d="M14.5 2 20 7.5h-5.5V2z" fill="#A1C2FA" opacity=".5"/><rect x="7" y="12" width="10" height="1.2" rx=".6" fill="#fff"/><rect x="7" y="14.5" width="8" height="1.2" rx=".6" fill="#fff"/><rect x="7" y="17" width="6" height="1.2" rx=".6" fill="#fff"/></svg>,
  'Google Sheets': <svg viewBox="0 0 24 24"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" fill="#0F9D58"/><path d="M14.5 2V7.5H20" fill="#87CEAC"/><path d="M14.5 2 20 7.5h-5.5V2z" fill="#87CEAC" opacity=".5"/><rect x="6.5" y="11.5" width="11" height="7.5" rx=".75" stroke="#fff" strokeWidth=".8" fill="none"/><line x1="12" y1="11.5" x2="12" y2="19" stroke="#fff" strokeWidth=".8"/><line x1="6.5" y1="14.25" x2="17.5" y2="14.25" stroke="#fff" strokeWidth=".8"/><line x1="6.5" y1="17" x2="17.5" y2="17" stroke="#fff" strokeWidth=".8"/></svg>,
  Outlook: <svg viewBox="0 0 24 24"><path d="M24 7.8v8.4c0 .44-.36.8-.8.8H14v-10h9.2c.44 0 .8.36.8.8z" fill="#0364B8"/><path d="M14 7l5 3.5L14 14V7z" fill="#0A2767" opacity=".5"/><path d="M24 7.8 14 14v-7l10 .8z" fill="#28A8EA"/><path d="M14 14l10 3v-9.2L14 14z" fill="#0078D4" opacity=".7"/><rect x="0" y="4.5" width="14" height="15" rx="1.5" fill="#0078D4"/><ellipse cx="7" cy="12" rx="3.8" ry="3.5" fill="#0078D4"/><ellipse cx="7" cy="12" rx="2.5" ry="2.2" fill="#fff"/></svg>,
  Slack: <svg viewBox="0 0 24 24"><path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A"/><path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.527 2.527 0 0 1 2.521 2.521 2.527 2.527 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z" fill="#36C5F0"/><path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.27 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.163 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D"/><path d="M15.163 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.163 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.27a2.527 2.527 0 0 1-2.52-2.523 2.527 2.527 0 0 1 2.52-2.52h6.315A2.528 2.528 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.523h-6.315z" fill="#ECB22E"/></svg>,
  'Microsoft Teams': <svg viewBox="0 0 24 24"><path d="M20.625 8.25h-6.375v9.375h6.375a1.125 1.125 0 0 0 1.125-1.125V9.375a1.125 1.125 0 0 0-1.125-1.125z" fill="#5059C9"/><circle cx="19.5" cy="5.25" r="2.25" fill="#5059C9"/><circle cx="15" cy="4.5" r="3" fill="#7B83EB"/><path d="M15.75 8.25H5.25A1.5 1.5 0 0 0 3.75 9.75v6a5.25 5.25 0 0 0 10.5 0v-6a1.5 1.5 0 0 0-1.5-1.5z" fill="#7B83EB"/><rect x="0" y="6" width="12" height="12" rx="1.2" fill="#4B53BC"/><path d="M9 10.5H6.75V16.5h-1.5V10.5H3V9h6v1.5z" fill="#fff"/></svg>,
  Zoom: <svg viewBox="0 0 24 24"><rect width="24" height="24" rx="4" fill="#2D8CFF"/><path d="M3.5 8.5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2v-5zm13 .7 4-2.7v9l-4-2.7V9.2z" fill="#fff"/></svg>,
  Dropbox: <svg viewBox="0 0 24 24"><path d="M7.1 1.8 1 5.9l6.1 4.1L1 14.1l6.1 4.1L12 14.1l4.9 4.1L23 14.1l-6.1-4.1L23 5.9l-6.1-4.1L12 5.9 7.1 1.8z" fill="#0061FF"/><path d="M7.1 1.8 1 5.9l6.1 4.1L12 5.9 7.1 1.8z" fill="#0061FF"/><path d="M16.9 1.8 12 5.9l5.1 4.1L23 5.9l-6.1-4.1z" fill="#0061FF"/><path d="M1 14.1l6.1 4.1L12 14.1l-4.9-4.1L1 14.1z" fill="#0061FF"/><path d="M12 14.1l4.9 4.1L23 14.1l-6.1-4.1L12 14.1z" fill="#0061FF"/><path d="M12 15.7l-4.9 3.3L12 22.3l4.9-3.3L12 15.7z" fill="#0061FF" opacity=".7"/></svg>,
  OneDrive: <svg viewBox="0 0 24 24"><path d="M9.5 7a6.5 6.5 0 0 1 6.33 5.03A5 5 0 0 1 19 22H6a5 5 0 0 1-.84-9.93A6.5 6.5 0 0 1 9.5 7z" fill="#0364B8"/><path d="M14.5 10a5 5 0 0 1 4.85 3.78A4 4 0 0 1 20 22H8a4 4 0 0 1-.54-7.97A5 5 0 0 1 14.5 10z" fill="#0078D4"/><path d="M10 13a4 4 0 0 1 7.98.5A3 3 0 0 1 19 22H7a3 3 0 0 1-.38-5.97A4 4 0 0 1 10 13z" fill="#28A8EA"/></svg>,
  Notion: <svg viewBox="0 0 24 24"><path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.98-.7-2.055-.607L2.58 2.56c-.467.047-.56.28-.374.466l2.253 1.182z" fill="#000"/><path d="M5.252 7.153v13.37c0 .72.373 1.026 1.167.98l14.523-.84c.793-.047 .886-.513.886-1.073V6.453c0-.56-.28-.84-.746-.793L6.14 6.5c-.513.047-.886.28-.886.653z" fill="#fff" stroke="#000" strokeWidth=".5"/><path d="M17.907 8.06c.093.42 0 .84-.42.886l-.7.14v9.847c-.606.326-1.166.513-1.633.513-.746 0-.933-.233-1.493-.933l-4.573-7.18v6.953l1.446.327s0 .84-1.167.84l-3.22.186c-.092-.187 0-.653.327-.746l.84-.233V9.553L5.799 9.46c-.094-.42.14-1.026.793-1.073l3.453-.233 4.76 7.274v-6.44l-1.213-.14c-.094-.513.28-.886.746-.933l3.37-.187z" fill="#000"/></svg>,
  Salesforce: <svg viewBox="0 0 24 24"><path d="M10.05 5.12a4.83 4.83 0 0 1 3.67-1.69c1.93 0 3.57 1.13 4.34 2.76a4.42 4.42 0 0 1 1.65-.32 4.52 4.52 0 0 1 0 9.04h-.1a3.6 3.6 0 0 1-5.97 2.44 4.29 4.29 0 0 1-3.56 1.88 4.27 4.27 0 0 1-3.93-2.59 3.98 3.98 0 0 1-.71.06 4.01 4.01 0 0 1-.93-7.91 4.83 4.83 0 0 1 5.54-3.67z" fill="#00A1E0"/></svg>,
  HubSpot: <svg viewBox="0 0 24 24"><path d="M17.5 8.2V5.83a2.17 2.17 0 1 0-1.52 0V8.2a5.07 5.07 0 0 0-2.86 1.67l-6.2-4.82a2.16 2.16 0 1 0-.88 1.14l6.06 4.71a5.1 5.1 0 0 0 .17 5.2l-1.83 1.83a2.04 2.04 0 0 0-.6-.1 2.08 2.08 0 1 0 2.08 2.08c0-.21-.04-.41-.1-.6l1.8-1.8a5.1 5.1 0 1 0 3.88-9.21z" fill="#FF7A59"/></svg>,
  Stripe: <svg viewBox="0 0 24 24"><rect width="24" height="24" rx="4" fill="#635BFF"/><path d="M11.1 9.28c0-.64.53-.89 1.4-.89a9.14 9.14 0 0 1 4.06.97V6.27a10.83 10.83 0 0 0-4.06-.75c-3.32 0-5.52 1.73-5.52 4.63 0 4.51 6.21 3.79 6.21 5.74 0 .76-.66 1.01-1.58 1.01a10.02 10.02 0 0 1-4.43-1.14v3.12a11.3 11.3 0 0 0 4.43.93c3.4 0 5.73-1.68 5.73-4.62 0-4.87-6.24-4-6.24-5.91z" fill="#fff"/></svg>,
  QuickBooks: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="11" fill="#2CA01C"/><path d="M7.5 8A2.5 2.5 0 0 0 5 10.5v3A2.5 2.5 0 0 0 7.5 16H9v-1.5H7.5a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1H9V8H7.5zm9 0H15v1.5h1.5a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H15V16h1.5a2.5 2.5 0 0 0 2.5-2.5v-3A2.5 2.5 0 0 0 16.5 8z" fill="#fff"/></svg>,
  Calendly: <svg viewBox="0 0 24 24"><path d="M16.23 7.18a5.78 5.78 0 0 0-4.08-1.7 5.79 5.79 0 0 0-5.78 5.8v1.45a5.79 5.79 0 0 0 5.78 5.79 5.78 5.78 0 0 0 4.08-1.7l.58.58A6.65 6.65 0 0 1 12.15 19.5a6.65 6.65 0 0 1-6.65-6.65v-1.7a6.65 6.65 0 0 1 6.65-6.65c1.74 0 3.34.67 4.66 1.8l-.58.88z" fill="#006BFF"/><circle cx="12.15" cy="12" r="2.8" fill="#006BFF"/><path d="M18.5 8.5l1.5-3M20 11.5h3M18.5 15.5l1.5 3" stroke="#006BFF" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  Clio: <svg viewBox="0 0 24 24"><rect width="24" height="24" rx="4" fill="#0B2D4A"/><path d="M6.5 16.5a5.5 5.5 0 1 1 7.78 0" stroke="#4FC3F7" strokeWidth="2" fill="none" strokeLinecap="round"/><circle cx="12" cy="12" r="2" fill="#4FC3F7"/><path d="M17 16.5h1.5" stroke="#4FC3F7" strokeWidth="2" strokeLinecap="round"/></svg>,
  DocuSign: <svg viewBox="0 0 24 24"><rect width="24" height="24" rx="4" fill="#FFC829"/><path d="M5 7h14v12H5V7z" fill="#1A1A24" opacity=".1"/><path d="M7 10h10v1.5H7zm0 3h8v1.5H7z" fill="#1A1A24" opacity=".3"/><path d="M14.5 14.5l-2 2-1-1" stroke="#1A1A24" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/><path d="M18 5l2 2-9 9-3-3 9-9z" fill="#1A1A24"/><path d="M18.5 4.5l1 1" stroke="#1A1A24" strokeWidth="2.5" strokeLinecap="round"/></svg>,
  'Clio Manage': <svg viewBox="0 0 24 24"><rect width="24" height="24" rx="4" fill="#0B2D4A"/><path d="M6.5 16.5a5.5 5.5 0 1 1 7.78 0" stroke="#4FC3F7" strokeWidth="2" fill="none" strokeLinecap="round"/><circle cx="12" cy="12" r="2" fill="#4FC3F7"/><rect x="16" y="15" width="3" height="4" rx=".5" fill="#4FC3F7"/></svg>,
  LawPay: <svg viewBox="0 0 24 24"><rect width="24" height="24" rx="4" fill="#1B365D"/><path d="M6 16h12" stroke="#5AC8FA" strokeWidth="2" strokeLinecap="round"/><path d="M9 16V9l3-2 3 2v7" stroke="#5AC8FA" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="11.5" r="1.5" fill="#5AC8FA"/></svg>,
  Trello: <svg viewBox="0 0 24 24"><rect width="24" height="24" rx="3.7" fill="#0079BF"/><rect x="4.5" y="4.5" width="6" height="13" rx="1.3" fill="#fff"/><rect x="13.5" y="4.5" width="6" height="9" rx="1.3" fill="#fff"/></svg>,
  Asana: <svg viewBox="0 0 24 24"><circle cx="12" cy="16.5" r="4.5" fill="#F06A6A"/><circle cx="4.5" cy="9" r="4.5" fill="#F06A6A"/><circle cx="19.5" cy="9" r="4.5" fill="#F06A6A"/></svg>,
  'Monday.com': <svg viewBox="0 0 24 24"><path d="M3.727 16.562a2.17 2.17 0 0 1-1.852-3.34l3.95-6.54a2.17 2.17 0 0 1 3.78.16l3.5 6.54a2.17 2.17 0 0 1-1.928 3.18H3.727z" fill="#FF3D57"/><path d="M12.545 16.562a2.17 2.17 0 0 1-1.852-3.34l3.95-6.54a2.17 2.17 0 0 1 3.78.16l3.5 6.54a2.17 2.17 0 0 1-1.928 3.18h-7.45z" fill="#FFCB00"/><circle cx="20.75" cy="15.25" r="2.25" fill="#00CA72"/></svg>,
  LinkedIn: <svg viewBox="0 0 24 24"><rect width="24" height="24" rx="3" fill="#0A66C2"/><path d="M7.5 9.5v8h-3v-8h3zm-1.5-1a1.75 1.75 0 1 1 0-3.5 1.75 1.75 0 0 1 0 3.5zm12.5 9h-3v-4.35c0-1.3-.85-1.65-1.2-1.65s-1.45.2-1.45 1.65V17.5h-3v-8h3v1.15s.9-1.15 2.5-1.15 2.95 1.15 2.95 3.75v4.25h.2z" fill="#fff"/></svg>,
  Xero: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="11" fill="#13B5EA"/><path d="M8 8l8 8M16 8l-8 8" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"/></svg>,
  Zendesk: <svg viewBox="0 0 24 24"><path d="M12 13.4V2h10l-10 11.4z" fill="#03363D"/><path d="M12 2a5 5 0 1 1-10 0h10z" fill="#03363D"/><path d="M12 10.6V22H2l10-11.4z" fill="#03363D"/><path d="M12 22a5 5 0 1 1 10 0H12z" fill="#03363D"/></svg>,
  PracticePanther: <svg viewBox="0 0 24 24"><rect width="24" height="24" rx="4" fill="#6C2BD9"/><path d="M12 4l-7 8h5v8h4v-8h5l-7-8z" fill="#fff"/></svg>,
  MyCase: <svg viewBox="0 0 24 24"><rect width="24" height="24" rx="4" fill="#2196F3"/><path d="M6 6h4v12H6zM14 6h4v12h-4z" fill="#fff" opacity=".9"/><path d="M10 10h4v4h-4z" fill="#fff"/></svg>,
  'Microsoft 365': <svg viewBox="0 0 24 24"><path d="M11 2H2v9h9V2z" fill="#F25022"/><path d="M22 2h-9v9h9V2z" fill="#7FBA00"/><path d="M11 13H2v9h9v-9z" fill="#00A4EF"/><path d="M22 13h-9v9h9v-9z" fill="#FFB900"/></svg>,
};

/* Three rows of brands — staggered for visual density */
const row1Apps = [
  'Gmail', 'Slack', 'Zoom', 'Salesforce', 'Stripe', 'DocuSign',
  'Calendly', 'Notion', 'HubSpot', 'LinkedIn',
];
const row2Apps = [
  'Google Calendar', 'Microsoft Teams', 'Dropbox', 'QuickBooks', 'Clio',
  'LawPay', 'Asana', 'Zendesk', 'PracticePanther', 'Xero',
];
const row3Apps = [
  'Google Drive', 'Google Docs', 'Google Sheets', 'Outlook', 'OneDrive',
  'Microsoft 365', 'Trello', 'Monday.com', 'MyCase', 'Clio Manage',
];

/* Duplicate each row enough times for seamless infinite scroll on 4K+ ultrawides */
const row1Loop = [...row1Apps, ...row1Apps, ...row1Apps, ...row1Apps, ...row1Apps, ...row1Apps];
const row2Loop = [...row2Apps, ...row2Apps, ...row2Apps, ...row2Apps, ...row2Apps, ...row2Apps];
const row3Loop = [...row3Apps, ...row3Apps, ...row3Apps, ...row3Apps, ...row3Apps, ...row3Apps];

// ─── Integrations Disclaimer ─────────────────────────────────────────────────

/* Faint ⓘ button — bottom-right corner of the section */
const InfoButton = styled.button`
  position: absolute;
  bottom: 10px;
  right: 14px;
  width: 20px;
  height: 20px;
  border: 1.5px solid rgba(0, 0, 0, 0.13);
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.2);
  font-size: 0.65rem;
  font-weight: 700;
  font-family: Georgia, serif;
  letter-spacing: 0;
  line-height: 1;
  padding: 0;
  transition: color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;
  opacity: 0.5;
  z-index: 10;

  &:hover {
    color: rgba(0, 0, 0, 0.55);
    border-color: rgba(0, 0, 0, 0.35);
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid var(--brand-gold-alt);
    outline-offset: 2px;
  }
`;

/* Backdrop overlay */
const ModalBackdrop = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
  position: fixed;
  inset: 0;
  z-index: 1000;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(2px);
  animation: ${({ $open }) => ($open ? 'fadeInBackdrop 0.15s ease' : 'none')};

  @keyframes fadeInBackdrop {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
`;

/* Modal card */
const ModalCard = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.18);
  padding: 2rem 2rem 1.75rem;
  max-width: 420px;
  width: calc(100% - 2.5rem);
  position: relative;
  animation: slideUp 0.18s ease;

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1.25rem 1.25rem;
  }
`;

const ModalCloseBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 14px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.1rem;
  color: #aaa;
  line-height: 1;
  padding: 4px;
  border-radius: 4px;
  transition: color 0.15s ease;

  &:hover { color: #333; }
  &:focus-visible { outline: 2px solid var(--brand-gold-alt); outline-offset: 2px; }
`;

const ModalTitle = styled.h3`
  font-family: 'Space Grotesk', 'Satoshi', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--brand-black);
  margin: 0 0 0.85rem;
  padding-right: 1.5rem;
`;

const ModalBody = styled.p`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.8rem;
  color: #666;
  line-height: 1.65;
  margin: 0;
`;

export function IntegrationsShowcase(): JSX.Element {
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  /* Close on Escape key */
  useEffect(() => {
    if (!disclaimerOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDisclaimerOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [disclaimerOpen]);

  /* Trap focus inside modal when open */
  useEffect(() => {
    if (disclaimerOpen && modalRef.current) {
      const focusable = modalRef.current.querySelectorAll<HTMLElement>('button, [href], [tabindex]');
      focusable[0]?.focus();
    }
  }, [disclaimerOpen]);

  return (
    <IntegrationsSection>
      <IntegrationsTitleWrapper>
        <IntegrationsTitle>easily integrates with any system</IntegrationsTitle>
      </IntegrationsTitleWrapper>
      <RowStack>
        <ScrollRowWrapper>
          <ScrollTrack $direction="left" $duration={150}>
            {row1Loop.map((name, idx) => (
              <AppItem key={`r1-${idx}`}>
                <AppIconWrapper>{appIcons[name] || <span>⚡</span>}</AppIconWrapper>
                <span>{name}</span>
              </AppItem>
            ))}
          </ScrollTrack>
        </ScrollRowWrapper>
        <ScrollRowWrapper>
          <ScrollTrack $direction="right" $duration={165}>
            {row2Loop.map((name, idx) => (
              <AppItem key={`r2-${idx}`}>
                <AppIconWrapper>{appIcons[name] || <span>⚡</span>}</AppIconWrapper>
                <span>{name}</span>
              </AppItem>
            ))}
          </ScrollTrack>
        </ScrollRowWrapper>
        <ScrollRowWrapper>
          <ScrollTrack $direction="left" $duration={144}>
            {row3Loop.map((name, idx) => (
              <AppItem key={`r3-${idx}`}>
                <AppIconWrapper>{appIcons[name] || <span>⚡</span>}</AppIconWrapper>
                <span>{name}</span>
              </AppItem>
            ))}
          </ScrollTrack>
        </ScrollRowWrapper>
      </RowStack>
      {/* Faint ⓘ icon — bottom-right of section */}
      <InfoButton
        aria-label="Integration trademark disclaimer"
        onClick={() => setDisclaimerOpen(true)}
      >
        i
      </InfoButton>

      {/* Disclaimer modal */}
      <ModalBackdrop
        $open={disclaimerOpen}
        onClick={(e) => { if (e.target === e.currentTarget) setDisclaimerOpen(false); }}
        role="dialog"
        aria-modal="true"
        aria-label="Integration disclaimer"
      >
        <ModalCard ref={modalRef}>
          <ModalCloseBtn onClick={() => setDisclaimerOpen(false)} aria-label="Close">
            ✕
          </ModalCloseBtn>
          <ModalTitle>Integration &amp; Trademark Notice</ModalTitle>
          <ModalBody>
            Integrations are powered securely via Composio. All third-party trademarks,
            logos, and icons referenced on this page are the property of their respective
            owners. Prest0 is not affiliated with, endorsed by, or sponsored by any of
            these trademark holders.
          </ModalBody>
        </ModalCard>
      </ModalBackdrop>
    </IntegrationsSection>
  );
}

